import React, { ReactElement, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import { STATIC_DATA_STATES, UnitsTypes } from '../../chart-components'
import { ModalButton, RestrictedContent, RestrictedMessage, RestrictedTitle } from '../../modal-components-style'
import { RestrictedRateOfPenetrationErrors, Errors, getEmptyRawRestrictedRateOfPenetrationEvent } from '../types'
import { fetchEntityOptions, fetchKpiGoalsWithFilter, fetchUnitOptions, TUBULAR_API } from '../../../api/api'
import { RawActivityEvent, RestrictedRateOfPenetration, SelectOptions, Uom, ValueUom } from '../../../utils/type'
import { KPI_COLUMN, ROP_TAB_ACRONYM, TUBULAR_TYPE_COLUMN } from '../constants'
import { getFormattedGoalLabel } from '../../../context/utils'
import { RestrictedRateOfPenetrationInformation } from './ROPTab/RestrictedRateOfPenetrationInformation'

interface ROPTabOptionsTemplate {
    restrictedRopType: SelectOptions[]
}

interface RestrictedROPTapProps {
    restrictedRateOfPenetrations: RestrictedRateOfPenetration[]
    errors: RestrictedRateOfPenetrationErrors
    validateRestrictedRateOfPenetrationFields: (
        restrictedPipe: RestrictedRateOfPenetration
    ) => { pass: boolean; errors: Errors }
    addRestrictedRateOfPenetration: (restrictedRateOfPenetration: RestrictedRateOfPenetration) => void
    updateCurrentRestrictedRateOfPenetration: (restrictedIndex: number, key: string, newValue: any) => void
    deleteRestrictedRateOfPenetration: (index: number) => void
    tubularsIntervalPerTime: boolean
    tubularType: SelectOptions | null
    showForm?: boolean
    assetFilterId?: string
    setIsAddRestrictedROPButtonDisabled: (isDisabled: boolean) => void
    tubularEvent: RawActivityEvent
}

export const ROPTab = ({
    restrictedRateOfPenetrations,
    errors,
    validateRestrictedRateOfPenetrationFields,
    addRestrictedRateOfPenetration,
    updateCurrentRestrictedRateOfPenetration,
    deleteRestrictedRateOfPenetration,
    tubularsIntervalPerTime,
    tubularType,
    assetFilterId,
    setIsAddRestrictedROPButtonDisabled,
    tubularEvent
}: RestrictedROPTapProps): ReactElement => {
    const [lengthUnitOptions, setLengthUnitOptions] = useState<Uom[]>([])
    const [ROPSpeedOptions, setROPSpeedOptions] = useState<ValueUom[] | null>(null)
    const [loadingROPSpeedOptions, setLoadingROPSpeedOptions] = useState(false)
    const [restrictedRopTypeList, setRestrictedRopTypeList] = useState<SelectOptions[]>([])
    const [filtersAppliedToGoals, setFiltersAppliedToGoals] = useState({ [KPI_COLUMN]: ROP_TAB_ACRONYM })
    const [isEventStarted, setIsEventStarted] = useState(
        tubularEvent.state === STATIC_DATA_STATES.STARTED_IN_EXECUTION ||
            tubularEvent.state === STATIC_DATA_STATES.FINISHED
    )
    const [emptyRawRestrictedRateOfPenetrationEvent, setEmptyRawRestrictedRateOfPenetrationEvent] = useState(
        getEmptyRawRestrictedRateOfPenetrationEvent()
    )
    const [isAddRestrictedPerformanceButtonDisabled, setIsAddRestrictedPerformanceButtonDisabled] = useState<boolean>(
        false
    )

    const isTubularEmpty = tubularEvent?.state === STATIC_DATA_STATES.EMPTY

    const setDefaultUomMDUnit = (defaultLengthUnit: Uom) => {
        const defaultStartMD = { ...emptyRawRestrictedRateOfPenetrationEvent.startMD }
        defaultStartMD.uom = defaultLengthUnit

        const defaultFinalMD = { ...emptyRawRestrictedRateOfPenetrationEvent.startMD }
        defaultFinalMD.uom = defaultLengthUnit

        setEmptyRawRestrictedRateOfPenetrationEvent(prev => ({
            ...prev,
            startMD: defaultStartMD,
            finalMD: defaultFinalMD
        }))
    }

    const setDefaultUnits = () => {
        const defaultLengthUnit = lengthUnitOptions.find(elem => elem?.name == 'm') || lengthUnitOptions[0]

        setDefaultUomMDUnit(defaultLengthUnit)
    }

    const fetchROPSpeedOptions = () => {
        setLoadingROPSpeedOptions(true)
        fetchKpiGoalsWithFilter(filtersAppliedToGoals, assetFilterId)
            .then(result =>
                (result.data ?? []).map(goal => ({
                    value: goal.target?.goals?.value,
                    uom: { name: goal.target?.goals?.uom },
                    ropSpeed: getFormattedGoalLabel(goal)
                }))
            )
            .then(ROPspeedOptions => {
                setROPSpeedOptions(ROPspeedOptions)
                setLoadingROPSpeedOptions(false)
            })
            .catch(err => setLoadingROPSpeedOptions(false))
    }

    useEffect(() => {
        fetchEntityOptions(TUBULAR_API).then((data: ROPTabOptionsTemplate) => {
            const hideUnrestrictedValue = data.restrictedRopType.filter(e => e.acronym != 'UN')
            setRestrictedRopTypeList(hideUnrestrictedValue)
        })
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setLengthUnitOptions(r.options)
            const defaultLengthUnit = r.options.find(elem => elem.name == 'm') || r[0]

            setDefaultUomMDUnit(defaultLengthUnit)
        })
    }, [])

    useEffect(() => {
        setDefaultUnits()
    }, [lengthUnitOptions])

    useEffect(() => {
        setFiltersAppliedToGoals(filters => ({ ...filters, [TUBULAR_TYPE_COLUMN]: tubularType?.acronym || null }))
    }, [tubularType?.acronym])

    useEffect(() => {
        setIsAddRestrictedROPButtonDisabled(isAddRestrictedPerformanceButtonDisabled)
    }, [isAddRestrictedPerformanceButtonDisabled])

    useEffect(() => {
        if (!restrictedRateOfPenetrations?.length) {
            setIsAddRestrictedPerformanceButtonDisabled(false)
        }
    }, [restrictedRateOfPenetrations])

    return (
        <>
            {isEventStarted && restrictedRateOfPenetrations?.length === 0 ? (
                <RestrictedMessage>
                    <i className="fa fa-empty-set" />
                    <RestrictedTitle>{i18n('No Restriction Applied')}</RestrictedTitle>
                    <RestrictedContent>
                        {i18n('There is no Restricted Performance defined for this tubular.')}
                    </RestrictedContent>
                </RestrictedMessage>
            ) : restrictedRateOfPenetrations?.length > 0 ? (
                <>
                    {restrictedRateOfPenetrations?.map((restrictedRateOfPenetration, index) => (
                        <RestrictedRateOfPenetrationInformation
                            restrictedRateOfPenetration={restrictedRateOfPenetration}
                            index={index}
                            tubularEvent={tubularEvent}
                            deleteRestrictedRateOfPenetration={deleteRestrictedRateOfPenetration}
                            filtersAppliedToGoals={filtersAppliedToGoals}
                            setFiltersAppliedToGoals={setFiltersAppliedToGoals}
                            errors={errors}
                            ROPSpeedOptions={ROPSpeedOptions}
                            loadingROPSpeedOptions={loadingROPSpeedOptions}
                            restrictedRopTypeList={restrictedRopTypeList}
                            fetchROPSpeedOptions={fetchROPSpeedOptions}
                            updateCurrentRestrictedRateOfPenetration={updateCurrentRestrictedRateOfPenetration}
                            assetFilterId={assetFilterId}
                            tubularsIntervalPerTime={tubularsIntervalPerTime}
                            lengthUnitOptions={lengthUnitOptions}
                            setIsAddRestrictedPerformanceButtonDisabled={setIsAddRestrictedPerformanceButtonDisabled}
                        />
                    ))}
                </>
            ) : (
                <>
                    <RestrictedMessage>
                        <i className="fa fa-empty-set" style={{ marginTop: '34px' }} />
                        <RestrictedTitle>No Restriction Applied</RestrictedTitle>
                        <RestrictedContent>
                            There is no Restricted Performance defined for this tubular. Click the Add Restricted button
                            to include.
                        </RestrictedContent>
                    </RestrictedMessage>
                </>
            )}
            {
                <ModalButton
                    styleType="action"
                    style={{ width: '100%' }}
                    disabled={isAddRestrictedPerformanceButtonDisabled}
                    onClick={() => {
                        const defaultLengthUnit =
                            lengthUnitOptions.find(elem => elem?.name == 'm') || lengthUnitOptions[0]
                        addRestrictedRateOfPenetration(getEmptyRawRestrictedRateOfPenetrationEvent(defaultLengthUnit))
                    }}
                >
                    <i style={{ paddingRight: '4px' }} className="far fa-plus-circle"></i>
                    {i18n('Add Restricted ROP')}
                </ModalButton>
            }
        </>
    )
}
